import React from 'react';

/**
 * PrivacyPage.jsx
 * 
 * Privacy Policy page for Tee Time Organizer
 * Required for Twilio toll-free verification compliance
 * 
 * Route: /privacy
 * 
 * Created: January 2026
 */

const PrivacyPage = () => {
  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.logoContainer}>
          <div style={styles.logoIcon}>⛳</div>
          <span style={styles.logoText}>Tee Time Organizer</span>
        </div>
        <a href="/" style={styles.homeLink}>← Back to Home</a>
      </header>

      {/* Main Content */}
      <main style={styles.main}>
        <div style={styles.card}>
          {/* Page Title */}
          <div style={styles.titleSection}>
            <div style={styles.iconCircle}>
              <span style={styles.icon}>🔒</span>
            </div>
            <h1 style={styles.title}>Privacy Policy</h1>
            <p style={styles.subtitle}>How we collect, use, and protect your information</p>
            <p style={styles.effectiveDate}>Effective Date: January 1, 2026</p>
          </div>

          {/* Introduction */}
          <section style={styles.section}>
            <p style={styles.paragraph}>
              Tee Time Organizer ("we," "us," or "our") is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your 
              information when you use our tee time management platform and related services.
            </p>
            <p style={styles.paragraph}>
              By using Tee Time Organizer, you agree to the collection and use of information 
              in accordance with this policy.
            </p>
          </section>

          {/* Information We Collect */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>1. Information We Collect</h2>
            
            <h3 style={styles.subTitle}>Personal Information</h3>
            <p style={styles.paragraph}>
              When you register or are added to a golf group, we may collect:
            </p>
            <ul style={styles.list}>
              <li>Name</li>
              <li>Email address</li>
              <li>Mobile phone number</li>
              <li>GHIN number (optional)</li>
              <li>Handicap index (optional)</li>
            </ul>

            <h3 style={styles.subTitle}>Usage Information</h3>
            <p style={styles.paragraph}>
              We automatically collect certain information when you use our service:
            </p>
            <ul style={styles.list}>
              <li>Tee time reservations and RSVP history</li>
              <li>Communication preferences</li>
              <li>Device and browser information</li>
              <li>IP address and general location</li>
            </ul>
          </section>

          {/* How We Use Your Information */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>2. How We Use Your Information</h2>
            <p style={styles.paragraph}>
              We use the information we collect to:
            </p>
            <ul style={styles.list}>
              <li>Manage your tee time reservations and group membership</li>
              <li>Send booking confirmations, reminders, and notifications</li>
              <li>Provide weather information for your scheduled tee times</li>
              <li>Notify you of schedule changes or cancellations</li>
              <li>Alert you when waitlist spots become available</li>
              <li>Improve our services and user experience</li>
              <li>Respond to your inquiries and support requests</li>
            </ul>
          </section>

          {/* SMS/Text Message Privacy */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>3. SMS/Text Message Privacy</h2>
            <div style={styles.highlightBox}>
              <p style={styles.paragraph}>
                <strong>If you opt-in to SMS notifications, the following applies:</strong>
              </p>
              <ul style={styles.list}>
                <li>Your phone number is used <strong>solely</strong> for sending tee time-related notifications</li>
                <li>We send only informational/transactional messages (confirmations, reminders, alerts)</li>
                <li>We do <strong>NOT</strong> send marketing or promotional text messages</li>
                <li>We do <strong>NOT</strong> sell, rent, or share your phone number with third parties for marketing purposes</li>
                <li>Message frequency varies based on your tee time activity (typically 2-6 messages per week during golf season)</li>
                <li>You can opt-out at any time by replying STOP to any message</li>
              </ul>
              <p style={styles.paragraph}>
                For complete SMS terms, please visit our{' '}
                <a href="/sms-terms" style={styles.link}>SMS Terms & Consent page</a>.
              </p>
            </div>
          </section>

          {/* Information Sharing */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>4. Information Sharing & Disclosure</h2>
            <p style={styles.paragraph}>
              We do <strong>not</strong> sell your personal information. We may share your information only in the following circumstances:
            </p>
            <ul style={styles.list}>
              <li><strong>With your golf group organizers:</strong> To manage tee times and group communications</li>
              <li><strong>With other group members:</strong> Your name may appear on tee time rosters visible to other players in your group</li>
              <li><strong>With service providers:</strong> We use trusted third-party services for email delivery (SendGrid), SMS messaging (Twilio), and weather data (Visual Crossing)</li>
              <li><strong>For legal compliance:</strong> If required by law, court order, or governmental authority</li>
              <li><strong>To protect rights:</strong> To protect our rights, privacy, safety, or property</li>
            </ul>
          </section>

          {/* Data Security */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>5. Data Security</h2>
            <p style={styles.paragraph}>
              We implement appropriate technical and organizational security measures to protect your personal information, including:
            </p>
            <ul style={styles.list}>
              <li>Encryption of data in transit (HTTPS/TLS)</li>
              <li>Secure database storage with access controls</li>
              <li>Regular security assessments</li>
              <li>Limited employee access to personal data</li>
            </ul>
            <p style={styles.paragraph}>
              However, no method of transmission over the Internet or electronic storage is 100% secure. 
              While we strive to protect your information, we cannot guarantee absolute security.
            </p>
          </section>

          {/* Data Retention */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>6. Data Retention</h2>
            <p style={styles.paragraph}>
              We retain your personal information for as long as your account is active or as needed to provide you services. 
              If you request account deletion or are removed from a golf group, we will delete or anonymize your 
              personal information within 30 days, unless we are required to retain it for legal or legitimate business purposes.
            </p>
          </section>

          {/* Your Rights */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>7. Your Rights & Choices</h2>
            <p style={styles.paragraph}>
              You have the following rights regarding your personal information:
            </p>
            <ul style={styles.list}>
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Opt-out of SMS:</strong> Reply STOP to any text message or visit our <a href="/sms-terms" style={styles.link}>SMS Terms page</a></li>
              <li><strong>Opt-out of Email:</strong> Click the unsubscribe link in any email notification</li>
            </ul>
            <p style={styles.paragraph}>
              To exercise any of these rights, please contact us at{' '}
              <a href="mailto:support@teetimeorganizer.net" style={styles.link}>support@teetimeorganizer.net</a>.
            </p>
          </section>

          {/* Third-Party Services */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>8. Third-Party Services</h2>
            <p style={styles.paragraph}>
              Our service uses the following third-party providers:
            </p>
            <ul style={styles.list}>
              <li><strong>SendGrid:</strong> Email delivery services</li>
              <li><strong>Twilio:</strong> SMS messaging services</li>
              <li><strong>Visual Crossing:</strong> Weather forecast data</li>
              <li><strong>Fly.io:</strong> Cloud hosting infrastructure</li>
            </ul>
            <p style={styles.paragraph}>
              These providers have their own privacy policies governing their use of your information.
            </p>
          </section>

          {/* Children's Privacy */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>9. Children's Privacy</h2>
            <p style={styles.paragraph}>
              Tee Time Organizer is not intended for children under 13 years of age. We do not knowingly 
              collect personal information from children under 13. If you are a parent or guardian and 
              believe your child has provided us with personal information, please contact us.
            </p>
          </section>

          {/* Changes to This Policy */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>10. Changes to This Privacy Policy</h2>
            <p style={styles.paragraph}>
              We may update this Privacy Policy from time to time. We will notify you of any changes by 
              posting the new Privacy Policy on this page and updating the "Effective Date" at the top. 
              We encourage you to review this Privacy Policy periodically for any changes.
            </p>
          </section>

          {/* Contact Us */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>11. Contact Us</h2>
            <p style={styles.paragraph}>
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div style={styles.contactBox}>
              <p style={styles.contactLine}><strong>Tee Time Organizer</strong></p>
              <p style={styles.contactLine}>Email: <a href="mailto:support@teetimeorganizer.net" style={styles.link}>support@teetimeorganizer.net</a></p>
              <p style={styles.contactLine}>Website: <a href="https://teetimeorganizer.net" style={styles.link}>teetimeorganizer.net</a></p>
            </div>
          </section>

          {/* Footer Note */}
          <div style={styles.footerNote}>
            <p style={styles.lastUpdated}>Last Updated: January 2026</p>
            <p style={styles.version}>Privacy Policy Version 1.0</p>
          </div>
        </div>
      </main>

      {/* Page Footer */}
      <footer style={styles.footer}>
        <p>© 2026 Tee Time Organizer. All rights reserved.</p>
        <div style={styles.footerLinks}>
          <a href="/sms-terms" style={styles.footerLink}>SMS Terms</a>
          <span style={styles.footerDivider}>|</span>
          <a href="/terms" style={styles.footerLink}>Terms of Service</a>
          <span style={styles.footerDivider}>|</span>
          <a href="mailto:support@teetimeorganizer.net" style={styles.footerLink}>Contact Support</a>
        </div>
      </footer>
    </div>
  );
};

// Styles
const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f5f7f5',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e0e7e0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  logoIcon: {
    fontSize: '28px',
  },
  logoText: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#2d6a4f',
  },
  homeLink: {
    color: '#2d6a4f',
    textDecoration: 'none',
    fontWeight: '500',
    fontSize: '15px',
  },
  main: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '40px 20px',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    padding: '48px',
  },
  titleSection: {
    textAlign: 'center',
    marginBottom: '40px',
  },
  iconCircle: {
    width: '80px',
    height: '80px',
    backgroundColor: '#e8f5e9',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
    border: '3px solid #2d6a4f',
  },
  icon: {
    fontSize: '36px',
  },
  title: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#1a1a1a',
    margin: '0 0 8px 0',
  },
  subtitle: {
    fontSize: '18px',
    color: '#666666',
    margin: '0 0 12px 0',
  },
  effectiveDate: {
    fontSize: '14px',
    color: '#888888',
    margin: '0',
  },
  section: {
    marginBottom: '32px',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#2d6a4f',
    marginBottom: '16px',
    marginTop: '0',
    paddingBottom: '8px',
    borderBottom: '1px solid #e8f5e9',
  },
  subTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '8px',
    marginTop: '20px',
  },
  paragraph: {
    fontSize: '16px',
    lineHeight: '1.7',
    color: '#333333',
    marginBottom: '12px',
  },
  list: {
    margin: '12px 0',
    paddingLeft: '24px',
    fontSize: '15px',
    lineHeight: '1.8',
    color: '#4b5563',
  },
  highlightBox: {
    backgroundColor: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: '12px',
    padding: '24px',
    marginTop: '16px',
  },
  contactBox: {
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '20px',
    marginTop: '16px',
  },
  contactLine: {
    fontSize: '15px',
    color: '#374151',
    margin: '0 0 8px 0',
  },
  link: {
    color: '#2d6a4f',
    textDecoration: 'none',
    fontWeight: '500',
  },
  footerNote: {
    textAlign: 'center',
    marginTop: '40px',
    paddingTop: '24px',
    borderTop: '1px solid #e5e7eb',
  },
  lastUpdated: {
    fontSize: '14px',
    color: '#888888',
    marginBottom: '4px',
  },
  version: {
    fontSize: '14px',
    color: '#666666',
    fontWeight: '500',
  },
  footer: {
    textAlign: 'center',
    padding: '24px',
    color: '#666666',
    fontSize: '14px',
    borderTop: '1px solid #e0e7e0',
    backgroundColor: '#ffffff',
  },
  footerLinks: {
    marginTop: '8px',
  },
  footerLink: {
    color: '#2d6a4f',
    textDecoration: 'none',
  },
  footerDivider: {
    margin: '0 12px',
    color: '#cccccc',
  },
};

export default PrivacyPage;
